import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import {
  Forecast,
  TargetPriceResearchFirmItem,
  TargetPriceResearchFirmResponse,
  ForecastType,
  ForecastResearchFirm,
  CreateForecastResearchFirm,
  UpdateForecastResearchFirm,
} from '../models/forecast.model';
import { URLS } from '../../../shared/constants/api-request.urls';

@Injectable({
  providedIn: 'root',
})
export class ForecastsService {
  private http = inject(HttpClient);

  getForecasts(stockId?: string, pageNumber?: number, pageSize?: number): Observable<any> {
    let params = new HttpParams();
    if (stockId) {
      params = params.set('stockId', stockId);
    }
    if (pageNumber) {
      params = params.set('pageNumber', pageNumber);
    }
    if (pageSize) {
      params = params.set('pageSize', pageSize);
    }
    return this.http.get<any>(URLS.FORECASTS, { params });
  }

  getForecastById(id: string): Observable<any> {
    return this.http.get<any>(`${URLS.FORECASTS}/${id}`);
  }

  createForecast(forecast: Forecast): Observable<any> {
    return this.http.post<any>(URLS.FORECASTS, forecast);
  }

  updateForecast(id: string, forecast: Forecast): Observable<any> {
    return this.http.put<any>(`${URLS.FORECASTS}/${id}`, forecast);
  }

  deleteForecast(id: string): Observable<any> {
    return this.http.delete<any>(`${URLS.FORECASTS}/${id}`);
  }

  createTargetPrice(targetPrice: any): Observable<any> {
    return this.http.post<any>(`${URLS.FORECASTS}/target-price`, targetPrice);
  }

  updateTargetPrice(id: string, targetPrice: any): Observable<any> {
    return this.http.put<any>(`${URLS.FORECASTS}/target-price/${id}`, targetPrice);
  }

  deleteTargetPrice(id: string): Observable<any> {
    return this.http.delete<any>(`${URLS.FORECASTS}/target-price/${id}`);
  }
  getTargetPrice(stockId: string): Observable<any> {
    const params = new HttpParams().set('stockId', stockId);
    return this.http
      .get<any>(`${URLS.FORECASTS}/target-price`, { params })
      .pipe(map((response) => response.data));
  }

  getTargetPriceById(id: string): Observable<any> {
    return this.http
      .get<any>(`${URLS.FORECASTS}/target-price/${id}`)
      .pipe(map((response) => response.data));
  }

  getResearchFirms(): Observable<any> {
    return this.http.get<any>(`${URLS.RESEARCH_FIRM}`).pipe(map((response) => response.data));
  }

  getTargetPriceResearchFirms(stockId: string): Observable<TargetPriceResearchFirmResponse[]> {
    return this.http
      .get<any>(`${URLS.FORECASTS}/target-price-research-firms?stockId=${stockId}`)
      .pipe(map((response) => response.data));
  }

  updateTargetPriceResearchFirms(targetPriceResearchFirms: {
    stockId: string;
    items: TargetPriceResearchFirmItem[];
  }): Observable<any> {
    return this.http
      .put<any>(`${URLS.FORECASTS}/target-price-research-firm`, targetPriceResearchFirms)
      .pipe(map((response) => response.data));
  }

  getForecastResearchFirms(
    stockId: string,
    forecastType: ForecastType,
    period: 'Quarterly' | 'Yearly'
  ): Observable<ForecastResearchFirm[]> {
    const params = new HttpParams()
      .set('stockId', stockId)
      .set('forecastType', forecastType)
      .set('period', period);
    return this.http
      .get<any>(`${URLS.FORECASTS}/research-firms`, { params })
      .pipe(map((response) => response.data));
  }

  getForecastResearchFirmById(id: string): Observable<any> {
    return this.http
      .get<any>(`${URLS.FORECASTS}/research-firms/${id}`)
      .pipe(map((response) => response.data));
  }

  createForecastResearchFirmEPS(
    forecastResearchFirm: CreateForecastResearchFirm
  ): Observable<ForecastResearchFirm> {
    return this.http
      .post<any>(`${URLS.FORECASTS}/research-firms/eps`, forecastResearchFirm)
      .pipe(map((response) => response.data as ForecastResearchFirm));
  }

  createForecastResearchFirmRevenue(
    forecastResearchFirm: CreateForecastResearchFirm
  ): Observable<ForecastResearchFirm> {
    return this.http
      .post<any>(`${URLS.FORECASTS}/research-firms/revenue`, forecastResearchFirm)
      .pipe(map((response) => response.data as ForecastResearchFirm));
  }

  updateForecastResearchFirm(
    id: string,
    forecastResearchFirm: UpdateForecastResearchFirm
  ): Observable<ForecastResearchFirm> {
    return this.http
      .put<any>(`${URLS.FORECASTS}/research-firms/${id}`, forecastResearchFirm)
      .pipe(map((response) => response.data as ForecastResearchFirm));
  }

  deleteForecastResearchFirm(id: string): Observable<void> {
    return this.http.delete<void>(`${URLS.FORECASTS}/research-firms/${id}`);
  }
}
